import {
  convertEventSelectToTarget,
  formatDataToLabelValue,
  FormFeedBack,
  Select,
} from '@repo/ui';
import {
  GetAllCurricularUnitsUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
} from 'domain/usecases';
import { FormikValues } from 'formik';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { loadCurricularUnitsUser } from 'presentation/config/store/curricular-units/curricular-units-slice';
import { loadGroupsUser } from 'presentation/config/store/groups/groups-slice';
import {
  setLoadingReportSelectCourse,
  setLoadingReportSelectCurricularUnit,
  setLoadingReportSelectGroup,
  setLoadingReportSelectOrganization,
  setLoadingReportSelectSemester,
} from 'presentation/config/store/load-select-report/loadingSelectReportSlice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import { loadSemestersUser } from 'presentation/config/store/semesters/semesters-slice';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CardBody, CardFooter, Col, Form, Label, Row } from 'reactstrap';

interface IProps {
  validation?: FormikValues;
  inputsChildren?: ReactNode;
  buttonsChildren: ReactNode;
  isGroupMulti?: boolean;
}

export const ReportsGeneralFilters = ({
  validation,
  inputsChildren,
  buttonsChildren,
  isGroupMulti = true,
}: IProps) => {
  const [filtersDisabled, setFiltersDisabled] = useState<boolean>(true);

  // Hooks
  const { currentUser } = useAuth();
  const { filterByCurricularUnit, filterBySemester } = currentUser;

  let isNotAManager = Number(currentUser?.activeProfile?.profileId) !== 2;

  const dispatch = useDispatch();

  const service = useService();
  const {
    organizationService,
    courseService,
    curricularUnitService,
    semesterService,
    groupService,
  } = service;

  const { coursesUser } = useAppSelector((state) => state.courses);
  const { organizationsUser } = useAppSelector((state) => state.organizations);
  const { semestersUser } = useAppSelector((state) => state.semesters);
  const { curricularUnitsUser } = useAppSelector(
    (state) => state.curricularUnits,
  );
  const { groupsUser } = useAppSelector((state) => state.groups);
  const {
    loadingReportSelectGroup,
    loadingReportSelectCourse,
    loadingReportSelectSemester,
    loadingReportSelectOrganization,
    loadingReportSelectCurricularUnit,
  } = useAppSelector((state) => state.loadingSelectReport);

  // Methods
  const requestGetAllOrganizationsUser = useCallback(async () => {
    dispatch(setLoadingReportSelectOrganization(true));
    await organizationService
      .getAllOrganizationsUser()
      .then((res) => dispatch(loadOrganizationsUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        dispatch(setLoadingReportSelectOrganization(false));
      });
  }, []);

  const requestGetAllCoursesUser = useCallback(async (entityId?: string) => {
    dispatch(setLoadingReportSelectCourse(true));

    await courseService
      .getAllCoursesUser({ entityId })
      .then((res) => dispatch(loadCoursesUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        dispatch(setLoadingReportSelectCourse(false));
      });
  }, []);

  const requestGetAllSemestersUser = useCallback(async (entityId: string) => {
    dispatch(setLoadingReportSelectSemester(true));
    await semesterService
      .getAllSemestersUser({ entityId })
      .then((res) => dispatch(loadSemestersUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        dispatch(setLoadingReportSelectSemester(false));
      });
  }, []);

  const requestGetAllCurricularUnitsUser = useCallback(
    async (params: GetAllCurricularUnitsUserUseCaseInterface.Params) => {
      dispatch(setLoadingReportSelectCurricularUnit(true));
      await curricularUnitService
        .getAllCurricularUnitsUser(params)
        .then((res) => dispatch(loadCurricularUnitsUser(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          dispatch(setLoadingReportSelectCurricularUnit(false));
        });
    },
    [],
  );

  const requestGetAllGroupsUser = useCallback(
    async (params: GetAllGroupsUserUseCaseInterface.Params) => {
      dispatch(setLoadingReportSelectGroup(true));
      await groupService
        .getAllGroupsUser(params)
        .then((res) => dispatch(loadGroupsUser(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          dispatch(setLoadingReportSelectGroup(false));
        });
    },
    [],
  );

  useEffect(() => {
    // useEffect responsible for the first filter calls
    requestGetAllCoursesUser();
    requestGetAllSemestersUser('');
    requestGetAllOrganizationsUser();
  }, []);

  useEffect(() => {
    // useEffect responsible for resetting Curricular Units and Groups' selects' options
    const selectedOrganization = validation.values.entity?.value;
    const selectedCourse = validation.values.course?.value;

    const selectedSemester = validation.values.semester?.value || '';
    const selectedCurricularUnit = validation.values.unit?.value || '';

    if ((selectedOrganization && selectedCourse) || isNotAManager) {
      requestGetAllCurricularUnitsUser({
        entityId: selectedOrganization,
        courseId: selectedCourse,
      });
      requestGetAllGroupsUser({
        courseId: selectedCourse,
        semesterId: selectedSemester,
        entityId: selectedOrganization,
        curricularUnitId: selectedCurricularUnit,
      });

      setFiltersDisabled(false);
    } else {
      setFiltersDisabled(true);
    }
  }, [
    validation.values.entity?.value,
    validation.values.course?.value,
    validation.values.semester?.value,
    validation.values.unit?.value,
  ]);

  return (
    <Form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      <CardBody>
        <Row>
          <Col xs={12} md={4} lg={4} className="mb-3">
            <Label htmlFor="entity" className="form-label">
              Organização:
            </Label>
            <Select
              isClearable
              isLoading={loadingReportSelectOrganization}
              id="entity"
              name="entity"
              placeholder="Todas as Organizações"
              value={validation.values.entity}
              onChange={(e) => {
                requestGetAllCoursesUser(e?.value || '');
                validation.setValues({
                  ...validation.values,
                  course: null,
                  semester: null,
                  unit: null,
                  groups: isGroupMulti ? [] : null,
                });

                validation.handleChange(
                  convertEventSelectToTarget('entity', e),
                );
              }}
              invalid={!!(validation.touched && validation.errors?.entity)}
              onBlur={validation.handleBlur}
              options={formatDataToLabelValue(
                organizationsUser as never,
                'id',
                'nome',
              )}
            />
            <FormFeedBack
              error={validation.errors?.entity}
              isInvalid={!!(validation.touched && validation.errors?.entity)}
            />
          </Col>

          <Col xs={12} md={4} lg={4} className="mb-3">
            <Label htmlFor="course" className="form-label">
              Curso:
            </Label>
            <Select
              isClearable
              isLoading={loadingReportSelectCourse}
              isDisabled={loadingReportSelectCourse}
              id="course"
              name="course"
              placeholder="Todos os Cursos"
              value={validation.values.course}
              onChange={(e) => {
                if (!e) {
                  validation.setValues({
                    ...validation.values,
                    unit: null,
                    groups: isGroupMulti ? [] : null,
                  });
                }

                validation.handleChange(
                  convertEventSelectToTarget('course', e),
                );
              }}
              onBlur={validation.handleBlur}
              options={formatDataToLabelValue(
                coursesUser as never,
                'id',
                'nome',
              )}
            />
          </Col>

          {filterByCurricularUnit && (
            <Col xs={12} md={4} lg={4} className="mb-3">
              <Label htmlFor="unit" className="form-label">
                Unidade Curricular:
              </Label>
              <Select
                isClearable
                id="unit"
                name="unit"
                isLoading={loadingReportSelectCurricularUnit}
                placeholder="Todas as Unidades Curriculares"
                value={validation.values.unit}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('unit', e),
                  );
                }}
                onBlur={validation.handleBlur}
                options={formatDataToLabelValue(
                  curricularUnitsUser as never,
                  'id',
                  'nome',
                )}
                isDisabled={
                  filtersDisabled || loadingReportSelectCurricularUnit
                }
              />
            </Col>
          )}

          {filterBySemester && (
            <Col xs={12} md={4} lg={4} className="mb-3">
              <Label htmlFor="semester" className="form-label">
                Semestre:
              </Label>
              <Select
                isClearable
                isLoading={loadingReportSelectSemester}
                id="semester"
                name="semester"
                placeholder="Todos os Semestres"
                value={validation.values.semester}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('semester', e),
                  );
                }}
                onBlur={validation.handleBlur}
                options={formatDataToLabelValue(
                  semestersUser as never,
                  'id',
                  'nome',
                )}
                isDisabled={filtersDisabled}
              />
            </Col>
          )}

          <Col xs={12} md={8} xl={4} className="mb-3">
            <Label htmlFor="groups" className="form-label">
              {isGroupMulti ? 'Grupo(s):' : 'Grupo:'}
            </Label>
            <Select
              isClearable
              isLoading={loadingReportSelectGroup}
              isMulti={isGroupMulti}
              id="groups"
              name="groups"
              placeholder="Todos os Grupos"
              value={validation.values.groups}
              onChange={(e) => {
                validation.handleChange(
                  convertEventSelectToTarget('groups', e),
                );
              }}
              onBlur={validation.handleBlur}
              options={formatDataToLabelValue(
                groupsUser as never,
                'id',
                'nome',
              )}
              isDisabled={filtersDisabled}
            />
          </Col>

          {inputsChildren}
        </Row>
      </CardBody>
      <CardFooter className="d-flex justify-content-end gap-3">
        {buttonsChildren}
      </CardFooter>
    </Form>
  );
};
